import {
  AtlasFiltersSchema,
  BoardFiltersSchema,
  HandoverFiltersSchema,
  StaffingFiltersSchema,
  StaffingFormFiltersSchema,
  type UserPreferences,
  type ViewFilters,
  WorklistFiltersSchema,
  WorklistListFiltersSchema,
} from '@projektverwaltung/shared';

/**
 * Sticky Filters (Issue #63) — der sichtneutrale Kern.
 *
 * Gesetzte Filter sollen Seitenwechsel, Reload UND Gerätewechsel überleben. Sie
 * liegen deshalb am Benutzerkonto (`users.preferences.view_filters`), nicht im
 * localStorage. Diese Datei enthält ausschließlich reine Funktionen und einen
 * React-freien Controller — die React-Anbindung steht in `use-sticky-filters.ts`.
 *
 * Der Controller kapselt die DREI Mechanismen, ohne die Filter verloren gehen.
 * Sie stammen aus dem Phase Board, wo jeder einzeln teuer erarbeitet wurde:
 *
 *  1. HYDRATIONS-GUARD — vorbelegt wird genau EINMAL pro Mount. Ohne ihn
 *     überschreibt eine nachlaufende Server-Antwort mit dem alten Stand die
 *     Filter, die der Nutzer eine Sekunde vorher gesetzt hat.
 *  2. OPTIMISTISCHES CACHE-UPDATE — der react-query-Cache wird SOFORT und
 *     synchron aktualisiert. Bei SPA-Navigation wird die Seite unmountet und
 *     beim Zurückkommen aus dem Cache neu aufgebaut; wäre dort noch der alte
 *     Stand, käme der Nutzer mit den Filtern von vorgestern zurück.
 *  3. NACHZÜGLER-FLUSH BEIM UNMOUNT — der Server-Write ist debounced. Wer einen
 *     Filter setzt und sofort wegnavigiert, verlöre ihn ohne diesen Flush.
 *
 * Wer die Mechanik nachbaut statt diesen Controller zu nutzen, baut genau diese
 * drei Fehler wieder ein.
 */

/** react-query-Key der Benutzerpräferenzen — geteilt mit Phase Board und Settings. */
export const PREFERENCES_QUERY_KEY = ['my-preferences'] as const;

/** Sichten, die ihre Filter merken. Schlüssel = Feld in `view_filters`. */
export type StickyFilterView = keyof ViewFilters;

/** Der Filter-Snapshot einer Sicht (ohne `undefined`). */
export type StickyFilterValues<V extends StickyFilterView> = NonNullable<ViewFilters[V]>;

export const DEFAULT_STICKY_DEBOUNCE_MS = 600;

// ---------------------------------------------------------------------------
// Reine Helfer
// ---------------------------------------------------------------------------

/**
 * JSON mit stabil sortierten Objekt-Schlüsseln. Zwei Snapshots mit denselben
 * Werten ergeben denselben String — unabhängig davon, in welcher Reihenfolge
 * die Schlüssel entstanden sind (der Server gibt JSON nicht zwingend in der
 * gesendeten Reihenfolge zurück). Array-Reihenfolge bleibt bedeutungstragend.
 */
export function stableStringify(value: unknown): string {
  return JSON.stringify(sortDeep(value)) ?? 'null';
}

function sortDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortDeep);
  if (value === null || typeof value !== 'object') return value;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  const out: Record<string, unknown> = {};
  for (const [k, v] of entries) out[k] = sortDeep(v);
  return out;
}

export type StickyFilterRead<V extends StickyFilterView> = {
  values: StickyFilterValues<V> | undefined;
  /**
   * true, wenn die Werte aus der Altablage `users.preferences.board_filters`
   * stammen. Der Controller erzwingt dann einen Write, damit die Werte
   * tatsächlich nach `view_filters` wandern — sonst bliebe die Migration
   * hängen, solange der Nutzer nichts umstellt.
   */
  legacy: boolean;
};

/**
 * Vertrag für die Sicht-Schemas: je Sicht ein Parser, der GENAU den Typ dieser
 * Sicht liefert.
 *
 * Die Annotation ist der eigentliche Punkt (nicht die Bequemlichkeit): sie nagelt
 * die Kopplung zwischen `VIEW_SCHEMAS` und `ViewFilters` fest. Eine FEHLENDE Sicht
 * — neu im Schema, hier nicht eingetragen — ist ab jetzt ein Typfehler statt eines
 * stillen Laufzeitproblems. Nebeneffekt: `parseStored` kommt ohne Cast aus.
 *
 * Was der Typ NICHT leistet: er fängt nicht jedes vertauschte Schema. Alle
 * Sicht-Schemas bestehen aus rein optionalen Feldern, deshalb ist z. B.
 * `board: HandoverFiltersSchema` weiterhin zuweisbar (`HandoverFilters` ist ein
 * gültiger `BoardFilters`). Nur die Gegenrichtung fällt auf — ein Schema, das
 * Felder liefert, die die Zielsicht nicht kennt. Die Zuordnung unten bleibt also
 * lesepflichtig.
 */
type ViewSchemas = {
  [V in StickyFilterView]: {
    safeParse: (
      raw: unknown,
    ) => { success: true; data: StickyFilterValues<V> } | { success: false };
  };
};

const VIEW_SCHEMAS: ViewSchemas = {
  board: BoardFiltersSchema,
  handover: HandoverFiltersSchema,
  staffing: StaffingFiltersSchema,
  atlas: AtlasFiltersSchema,
  worklist: WorklistFiltersSchema,
  worklist_list: WorklistListFiltersSchema,
  staffing_form: StaffingFormFiltersSchema,
};

/**
 * `GET /v1/users/me/preferences` liefert den rohen JSON-Inhalt der Spalte, ohne
 * ihn zu prüfen. Deshalb hier die Validierung: ein unbrauchbarer Blob (händisch
 * geändert, Altbestand) darf die Sicht nicht kaputt machen, sondern zählt als
 * „nichts gespeichert" und wird beim nächsten Write geheilt.
 */
function parseStored<V extends StickyFilterView>(
  view: V,
  raw: unknown,
): StickyFilterValues<V> | undefined {
  if (raw === undefined || raw === null) return undefined;
  const result = VIEW_SCHEMAS[view].safeParse(raw);
  return result.success ? result.data : undefined;
}

/**
 * Gespeicherte Filter einer Sicht lesen — inklusive Abwärtskompatibilität:
 * Für das Phase Board ist `board_filters` die Altablage und wird weiter gelesen,
 * solange `view_filters.board` fehlt. Ein Nutzer verliert seine produktiv
 * gesetzten Filter durch das Deployment also nicht.
 */
export function readStickyFilters<V extends StickyFilterView>(
  prefs: UserPreferences | undefined,
  view: V,
): StickyFilterRead<V> {
  const fromViewFilters = parseStored(view, prefs?.view_filters?.[view]);
  if (fromViewFilters !== undefined) return { values: fromViewFilters, legacy: false };
  if (view === 'board') {
    const legacyValues = parseStored(view, prefs?.board_filters);
    if (legacyValues !== undefined) return { values: legacyValues, legacy: true };
  }
  return { values: undefined, legacy: false };
}

/**
 * Präferenzen mit dem neuen Snapshot einer Sicht zusammenführen. Genutzt für das
 * optimistische Cache-Update UND als Payload des PATCH: `view_filters` geht
 * vollständig raus. Die API merged `view_filters` inzwischen zwar selbst je Sicht
 * (`mergePreferences` in `apps/api/src/routes/users.ts`) — die vollständige Payload
 * bleibt trotzdem, damit ein Rollback der API die Filter der anderen Sichten nicht
 * wegräumt.
 *
 * `board_filters` bleibt bewusst stehen: es ist die Rückfallebene, falls das
 * Deployment zurückgerollt wird.
 */
export function mergeStickyFilters<V extends StickyFilterView>(
  prefs: UserPreferences | undefined,
  view: V,
  snapshot: StickyFilterValues<V>,
): UserPreferences {
  return {
    ...(prefs ?? {}),
    view_filters: { ...(prefs?.view_filters ?? {}), [view]: snapshot },
  };
}

/**
 * Einmalige Übernahme eines bisher nur lokal (localStorage) gemerkten Wertes.
 * Der lokale Wert füllt ausschließlich Lücken — steht am Konto bereits etwas,
 * gewinnt das Konto (sonst würde ein altes Gerät die Wahl eines neuen
 * dauerhaft zurückdrehen).
 */
export function applyLocalTakeover<T extends object>(
  stored: T | undefined,
  local: Partial<T> | null | undefined,
): T | undefined {
  const fill = Object.entries(local ?? {}).filter(([, v]) => v !== undefined);
  if (fill.length === 0) return stored;
  const merged: Record<string, unknown> = { ...(stored ?? {}) };
  for (const [key, value] of fill) {
    if (merged[key] === undefined) merged[key] = value;
  }
  return merged as T;
}

/**
 * Startwert einer gemerkten Auswahl gegen einen konfigurierten Standard abwägen
 * (Phase Board: `view_filters.board.lens` gegen `default_phase_lens`).
 *
 * Die Regel, und warum sie so ist:
 *
 *  - Eine GEMERKTE Wahl gewinnt immer. Sie ist geräteübergreifend und darf von
 *    keinem Standard verdrängt werden — sonst überschriebe jeder Seitenaufruf am
 *    zweiten Gerät die Wahl, die am ersten getroffen wurde (genau dieser
 *    Datenverlust war der Befund zu Issue #63).
 *  - Der konfigurierte Standard greift nur, solange keine Wahl existiert, und
 *    zählt selbst NICHT als Wahl (`chosen: false`). Deshalb wandert er auch nicht
 *    ungefragt ans Konto: wer nie umgeschaltet hat, folgt weiterhin dem Standard
 *    aus den Einstellungen — auch wenn dieser später geändert wird.
 */
export function resolveStartValue<T>(
  remembered: T | undefined,
  configuredDefault: T | undefined,
): { value: T | undefined; chosen: boolean } {
  if (remembered !== undefined) return { value: remembered, chosen: true };
  return { value: configuredDefault, chosen: false };
}

export type PersonaDefaultDecision = {
  /** Persona-Default-Filter jetzt setzen? */
  apply: boolean;
  /**
   * Diesen Lauf als „für diese Persona entschieden" markieren? Nur ein markierter
   * Lauf verbraucht den Erstbesuch — siehe unten.
   */
  mark: boolean;
};

/**
 * Persona-Default-Filter gegen gemerkte Filter abwägen (Portfolio Atlas, Issue #63).
 *
 * Reine Funktion, damit die Abfolge ohne React testbar ist. Seit Issue #64 gibt es
 * zusätzlich Komponententests (`*.test.tsx`, jsdom) — die reine Funktion bleibt
 * trotzdem der erste Zugriff: sie ist schneller und beschreibt die Regel direkt.
 *
 * Die drei Regeln:
 *
 *  1. Solange die Präferenzen unterwegs sind, passiert NICHTS — sonst überschrieben
 *     die Defaults die gleich eintreffenden gemerkten Filter.
 *  2. Beim ERSTEN Lauf gewinnen gemerkte Filter gegen die Defaults. Ein späterer
 *     Persona-WECHSEL setzt die Defaults dagegen weiterhin.
 *  3. Scheitert die Präferenz-Anfrage, greifen die Defaults trotzdem (sie hingen
 *     vor #63 nicht an dieser Anfrage) — aber der Fehlerlauf VERBRAUCHT den
 *     Erstbesuch nicht (`mark: false`). Sonst gilt ein späterer, erfolgreicher
 *     Refetch (react-query: `retry`, `refetchOnWindowFocus`) nicht mehr als erster
 *     Lauf, die Defaults überschrieben die gerade hydrierten gemerkten Filter — und
 *     der Save-Effekt schriebe sie ans Konto.
 */
export function decidePersonaDefaults(input: {
  hydrated: boolean;
  failed: boolean;
  hadStored: boolean;
  /** Persona, für die zuletzt entschieden wurde; `null` = noch nie (Erstbesuch). */
  appliedFor: string | null;
}): PersonaDefaultDecision {
  if (!input.hydrated && !input.failed) return { apply: false, mark: false };
  const isFirstRun = input.appliedFor === null;
  if (isFirstRun && input.hadStored) return { apply: false, mark: true };
  return { apply: true, mark: !input.failed };
}

// ---------------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------------

type TimerHandle = ReturnType<typeof setTimeout>;

export type StickyFilterTimers = {
  set: (fn: () => void, ms: number) => TimerHandle;
  clear: (handle: TimerHandle) => void;
};

export type StickyFilterControllerOptions<T extends object> = {
  /** Server-Write (debounced). */
  persist: (snapshot: T) => void;
  /** Optimistisches, synchrones Cache-Update. */
  updateCache: (snapshot: T) => void;
  debounceMs?: number;
  /** Nur für Tests injizierbar. */
  timers?: StickyFilterTimers;
};

export type StickyFilterHydrateInput<T extends object> = {
  stored: T | undefined;
  legacy?: boolean | undefined;
  local?: Partial<T> | null | undefined;
  apply: (values: T) => void;
};

export type StickyFilterController<T extends object> = {
  /** Einmalige Vorbelegung. Liefert false, wenn schon hydriert wurde. */
  hydrate: (input: StickyFilterHydrateInput<T>) => boolean;
  /** Aktuellen Filterstand melden (idempotent, verwirft identische Stände). */
  save: (snapshot: T) => void;
  /**
   * Ausstehenden Write sofort abschicken — beim Unmount aufzurufen. Bewusst OHNE
   * „stillgelegt"-Flag: React StrictMode mountet in DEV doppelt (mount → cleanup →
   * mount); ein einmal stillgelegter Controller würde dort nie wieder schreiben.
   */
  flush: () => void;
  readonly hydrated: boolean;
  /** true, wenn beim Hydrieren gespeicherte Werte vorlagen. */
  readonly hadStored: boolean;
};

const defaultTimers: StickyFilterTimers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (handle) => clearTimeout(handle),
};

export function createStickyFilterController<T extends object>(
  options: StickyFilterControllerOptions<T>,
): StickyFilterController<T> {
  const timers = options.timers ?? defaultTimers;
  const debounceMs = options.debounceMs ?? DEFAULT_STICKY_DEBOUNCE_MS;

  let hydrated = false;
  let hadStored = false;
  /** Zuletzt in Cache/Server geschriebener Stand — verhindert identische Writes. */
  let lastWritten: string | null = null;
  /** Noch nicht abgeschickter Stand (Debounce läuft). */
  let pending: T | null = null;
  let timer: TimerHandle | null = null;

  const clearTimer = () => {
    if (timer !== null) {
      timers.clear(timer);
      timer = null;
    }
  };

  // (3) Nachzügler beim Verlassen der Seite.
  const flush = () => {
    clearTimer();
    const snapshot = pending;
    pending = null;
    if (snapshot) options.persist(snapshot);
  };

  return {
    hydrate(input) {
      // (1) Hydrations-Guard.
      if (hydrated) return false;
      hydrated = true;
      hadStored = input.stored !== undefined;
      const merged = applyLocalTakeover(input.stored, input.local);
      if (merged !== undefined) input.apply(merged);
      // Der gerade vorgelegte Stand gilt als „geschrieben", damit der unmittelbar
      // folgende save() keinen identischen Write auslöst. Ausnahme: Altablage —
      // dort MUSS einmal geschrieben werden, damit die Migration durchläuft.
      lastWritten =
        input.stored === undefined || input.legacy ? null : stableStringify(input.stored);
      return true;
    },
    save(snapshot) {
      if (!hydrated) return;
      const key = stableStringify(snapshot);
      if (key === lastWritten) return;
      // Leerer Schnappschuss auf einem Konto, das für diese Sicht noch NICHTS
      // gespeichert hat: kein schreibwürdiger Zustand — der Write trägt null
      // Information und kann nur schaden. Grund: `save()` schickt bei jedem
      // Aufruf immer den KOMPLETTEN `view_filters`-Baum aus dem lokalen
      // react-query-Cache (Rückfallebene gegen eine zurückgerollte API, siehe
      // `mergeStickyFilters`). Öffnet Fenster B (z. B. Projektdetail →
      // Staffing-Tab, der ohne jede Nutzerhandlung mit einem leeren Snapshot
      // speichert) mit einem bis zu `staleTime` alten Cache, während Fenster A
      // kurz zuvor echte Filter gesetzt hat, überschriebe dieser Leer-Write die
      // frischen Filter von A — der Nutzer verliert Einstellungen, ohne selbst
      // etwas getan zu haben (Review vor dem Produktiv-Deploy, PV-036-Folgefund).
      // `lastWritten === null` heißt „am Konto steht nichts UND es ist keine
      // Altablagen-Migration" (die erzwingt weiter unten immer einen Write,
      // s. `hydrate()`) — kombiniert mit einem leeren Snapshot ist das exakt der
      // schädliche Fall. Bewusstes Löschen aller Filter bleibt unberührt: dort
      // steht bereits ein gespeicherter Stand, `lastWritten` ist dann nicht
      // `null`, der Write findet also statt.
      if (lastWritten === null && Object.keys(snapshot).length === 0) return;
      lastWritten = key;
      // (2) Cache sofort — der SPA-Remount liest von hier.
      options.updateCache(snapshot);
      pending = snapshot;
      clearTimer();
      timer = timers.set(() => {
        timer = null;
        flush();
      }, debounceMs);
    },
    flush,
    get hydrated() {
      return hydrated;
    },
    get hadStored() {
      return hadStored;
    },
  };
}
